import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import InfoTooltip from "./InfoTooltip";

export default function FancySelectNativeLook({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  disabled = false,
  className = "",
  tooltipPosition = "left",
}) {
  const [open, setOpen] = useState(false);
  const [dropdownUp, setDropdownUp] = useState(false);
  const [dropdownStyles, setDropdownStyles] = useState({});
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const updateDropdownPosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = Math.min(240, options.length * 40 + 20); // max-h-60 ≈ 240px
    const spaceBelow = window.innerHeight - rect.bottom;
    const showUp = spaceBelow < dropdownHeight + 10;

    setDropdownUp(showUp);
    setDropdownStyles({
      position: "fixed",
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: 9999,
      top: showUp
        ? `${Math.max(8, rect.top - dropdownHeight - 8)}px`
        : `${rect.bottom + 4}px`,
      maxHeight: `${dropdownHeight}px`,
    });
  };

  useEffect(() => {
    const handlePointerDown = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);

    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  // Detectar si hay espacio hacia abajo para mostrar dropdown
  useEffect(() => {
    if (!open) return;

    updateDropdownPosition();

    window.addEventListener("resize", updateDropdownPosition);
    document.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      document.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [open, options.length]);

  const selected = options.find((opt) => String(opt.value) === String(value));
  const selectedLabel = selected?.label || "";

  return (
    <div className={`relative w-full overflow-visible ${className}`} ref={ref}>
      {label && (
        <label className="text-[12px] text-[#686873] block pb-1">{label}</label>
      )}

      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onPointerDown={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.stopPropagation();
          setOpen((p) => !p);
        }}
        className={`
          w-full relative
          rounded-xl border border-slate-200 bg-slate-50
          mt-3
          px-3 py-2 pr-10
          text-left text-[14px] text-slate-900
          shadow-sm
          outline-none
          ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-slate-300"}
        `}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedLabel ? "" : "text-[#A7A7A7]"}>
          {selectedLabel || placeholder}
        </span>

        <span
          className={`
            pointer-events-none
            absolute right-3 top-4 -translate-y-1/2
            w-2 h-2 border border-[#686873] border-t-0 border-l-0 rotate-45
            transition-transform
            ${open ? "rotate-[225deg] translate-y-[-30%]" : ""}
          `}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            role="listbox"
            style={dropdownStyles}
            className="bg-white border border-[#F2F2F2] rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {options.length === 0 && (
              <div className="px-4 py-2 text-[14px] text-[#98999A]">
                No options available
              </div>
            )}

            {options.map((opt) => {
              const isSelected = String(value) === String(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`
                    w-full text-left
                    px-4 py-2.5 text-[14px]
                    transition-colors
                    ${isSelected ? "bg-[#F0F5F0] text-[#0F0F0F]" : "text-[#101010]"}
                    hover:bg-[#F7F9FA]
                  `}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium leading-snug">{opt.label}</span>
                    {opt.description && (
                      <InfoTooltip description={opt.description} anchor={opt.anchor || ""} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}